import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Cake, Send, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

/**
 * Tiny Admin dashboard tile — shows today's birthday matches so Rohan sees
 * who's been greeted without opening logs. Lists all matched clients with
 * their status (Sent / Pending / No email on file). "Send now" runs the
 * cron sweep manually so any pending rows go out immediately.
 */
export default function BirthdayTile() {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/birthdays/today");
      setData(data);
    } catch { /* silent — tile just stays dim */ }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/admin/birthday-check/run-now");
      if (res.sent > 0) toast.success(`${res.sent} birthday email${res.sent === 1 ? "" : "s"} sent.`);
      else toast.info("No pending birthdays — all set.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send.");
    } finally { setSending(false); }
  };

  const clients = data?.clients || [];
  const sentCount = data?.sent_count ?? 0;
  const pendingCount = data?.pending_count ?? 0;
  const total = clients.length;

  return (
    <Card className="card-lift border-border bg-white p-6" data-testid="birthday-tile">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-accent/10 text-accent">
          <Cake className="h-4 w-4" />
        </div>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Fires daily 10:00 IST
        </span>
      </div>
      <div className="overline mb-1 text-muted-foreground">Today's birthdays</div>
      <div className="font-dashboard text-lg font-bold text-foreground" data-testid="birthday-tile-headline">
        {total === 0
          ? "No clients today"
          : `${total} client${total === 1 ? "" : "s"} today`}
      </div>
      <div className="mt-1 text-xs text-muted-foreground" data-testid="birthday-tile-breakdown">
        {sentCount} sent · {pendingCount} pending
        {clients.some((c) => c.is_leap_shift) && (
          <span className="ml-1 italic">(incl. Feb-29 shifted to today)</span>
        )}
      </div>

      {clients.length > 0 && (
        <ul className="mt-3 max-h-40 space-y-1 overflow-y-auto rounded-md border border-border bg-muted/20 p-2 text-xs"
            data-testid="birthday-tile-list">
          {clients.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-2"
                data-testid={`birthday-row-${c.id}`}>
              <span className="truncate" title={c.full_name}>{c.full_name}</span>
              {c.status === "Sent" ? (
                <Badge variant="secondary" className="gap-1 text-[10px]">
                  <CheckCircle2 className="h-3 w-3 text-emerald-600" /> Sent
                </Badge>
              ) : c.status === "No email on file" ? (
                <Badge variant="secondary" className="gap-1 text-[10px] text-amber-700">
                  <Mail className="h-3 w-3" /> No email
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-[10px]">Pending</Badge>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4">
        <Button
          size="sm"
          onClick={runNow}
          disabled={sending || pendingCount === 0}
          className="gap-1"
          data-testid="birthday-tile-send-now"
        >
          <Send className="h-3.5 w-3.5" />
          {sending ? "Sending…" : (pendingCount === 0 ? "Nothing pending" : `Send ${pendingCount} now`)}
        </Button>
      </div>
    </Card>
  );
}
